import { observer } from 'mobx-react-lite'
import classNames from 'classnames'
import { useEffect, useRef, useState } from 'react'
import { Tooltip } from 'shared/ui/Tooltip'
import {
  type ITextFieldProps,
  TextField,
  makeTextFieldAutoFocus,
  makeTextFieldError,
  makeTextFieldHighlightLimit,
} from 'shared/ui/TextField'
import { makeTextFieldAutoWidth } from 'shared/ui/TextField/ActionsMain/TextFieldAutoWidth'
import styles from './styles.module.scss'

type IEditableTitleProps = {
  tooltipLabel?: string
  value: string
  onChange: (value: string) => void
  error?: string | null
  limit?: number
  textFieldProps?: ITextFieldProps
  maxWidth?: number
  minWidth?: number
  isAutoFocus?: boolean
}

export const EditableTitle = observer(
  ({
    tooltipLabel,
    value,
    onChange,
    error,
    limit,
    textFieldProps,
    maxWidth,
    minWidth,
    isAutoFocus,
  }: IEditableTitleProps) => {
    const nameInputRef = useRef<HTMLInputElement>(null)
    const [limitReached, setLimitReached] = useState(false)
    const [isOverflowing, setIsOverflowing] = useState(false)

    useEffect(() => {
      const input = nameInputRef.current
      if (input) {
        setIsOverflowing(input.scrollWidth > input.clientWidth)
      }
    }, [value])

    const getTooltipLabel = () => {
      if (limitReached && limit) {
        return `${limit}-character limit reached`
      }
      if (isOverflowing) {
        return value
      }
      if (tooltipLabel) {
        return tooltipLabel
      }

      return ''
    }

    return (
      <Tooltip label={getTooltipLabel()}>
        <TextField
          className={classNames(styles.title, {
            [styles.error]: !!error,
          })}
          variant='outline'
          InputProps={{
            placeholder: 'Name',
            ref: nameInputRef,
            ...textFieldProps?.InputProps,
          }}
          onChange={onChange}
          value={value}
          mainActions={[
            makeTextFieldAutoWidth({ minWidth: minWidth ?? 80, maxWidth: maxWidth ?? 500 }),
            makeTextFieldHighlightLimit({
              limit: limit ?? Number.POSITIVE_INFINITY,
              onChange: setLimitReached,
            }),
            makeTextFieldAutoFocus({
              withFocus: isAutoFocus || !value,
            }),
          ]}
          rightActions={[
            makeTextFieldError({ placement: 'right' }),
            ...(textFieldProps?.rightActions ?? []),
          ]}
          error={error}
          {...textFieldProps}
        />
      </Tooltip>
    )
  }
)
